<template>
    <div class="layout-main-container">
        <div class="layout-main">
            <div id="cover_correction_controls" dir="rtl">
                <Chip v-for="b in selectedBooks" :label="b.title" :id="b.id" :key="b" :closable="true"
                    @remove="handleChipRemove" />
                <div>
                    <Button :label="$t('CORRECT')" @click="correctCovers" :disabled="!canCorrectCovers" />
                </div>
            </div>
            <BookGrid ref="bookGrid" :books="books" :pageSize="pageSize" :totalBooksCount="totalBooksCount"
                :selectableBooks="true" @bookSelected="bookCheckEvent" @bookUnselected="bookUnCheckEvent"
                @filterTriggered="filterBooks"
                @pageRequested="pageRequest => loadPage(pageRequest.number, pageRequest.filter)" />
        </div>
    </div>
</template>

<script setup>
import { computed, onErrorCaptured, onMounted, ref } from 'vue'
import { handleError } from '@/helpers/ErrorHandler'
import { useToast } from 'primevue/usetoast'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'
import BookGrid from '@/components/BookGrid.vue'
import Chip from '@/components/Chip.vue'
import bookService from '@/service/BookService'
import remove from 'lodash/remove'

const authStore = useAuthStore()
const toast = useToast()
const i18n = useI18n()

const pageSize = 20
const totalBooksCount = ref(0)

const books = ref([])
const selectedBooks = ref([])

const bookGrid = ref(null)

onMounted(async () => {
    try {
        await loadPage(0)
    } catch (error) {
        handleError(authStore, toast, i18n, error)
    }
})

onErrorCaptured((err, instance, info) => {
    return handleError(authStore, toast, i18n, err, instance, info)
})

const filterBooks = async (filter) => {
    await loadPage(0, filter)
}

const loadPage = async (number, filter) => {
    const response = await bookService.getAllBooks(number, pageSize, filter)
    books.value = response.collection
    totalBooksCount.value = response.total
    if (selectedBooks.value.length > 0) {
        const selectedBooksIds = selectedBooks.value.map(b => b.id)
        books.value.filter(b => selectedBooksIds.includes(b.id)).forEach(b => b.selected = true)
    }
}

const canCorrectCovers = computed(() => {
    return selectedBooks.value.length > 0
})

const correctCovers = async () => {
    const createResult = await bookService.correctCovers(selectedBooks.value.map(b => b.id))
    await cleanView()
    toast.add({ severity: 'info', summary: createResult.message, detail: createResult.message, life: 3000 })
}

const cleanView = async () => {
    bookGrid.value.resetFilter()
    bookGrid.value.resetPagination()
    selectedBooks.value = []
    await loadPage(0)
}

// events

const bookCheckEvent = bookId => {
    selectedBooks.value.push(books.value.filter(b => b.id === bookId)[0])
}

const bookUnCheckEvent = bookId => {
    remove(selectedBooks.value, b => b.id === bookId)
}

const handleChipRemove = (bookId) => {
    const book = books.value.filter(b => b.id === bookId)[0]
    if (book !== undefined && book !== null) {
        // if in current page
        book.selected = false
    }
    remove(selectedBooks.value, b => b.id === bookId)
}

</script>

<style scoped>
#cover_correction_controls>* {
    margin-bottom: 1em;
}
</style>
